import { getStoredHostSession } from "./webAuth";

const API_URL = process.env.REACT_APP_API_URL;

// The Free plan's monthly presentation allowance, as the backend reports it.
// A presentation is one deck on one local day — reconnects, rehearsals and the
// same lesson for several classes that day all count once.
export type PresentationAllowance = {
  used: number;
  limit: number;
  // ISO-8601: local midnight on the next sign-up day of the month (when the
  // account was created), as a UTC instant.
  resetsAt: string | null;
  // False in shadow mode: counted, but nobody is stopped. The controller hides
  // the meter until it is true.
  enforced: boolean;
  blocked: boolean;
};

// The browser's IANA zone. The allowance counts days and months in it, so a
// US evening lecture isn't split across two UTC days.
export const browserTimeZone = (): string | undefined => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
};

// "1 October" — the date the allowance resets, in the host's own locale.
export const formatResetDate = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { day: "numeric", month: "long" });
};

// The meter only speaks once the allowance is being enforced and the host is
// close enough to the limit for it to matter.
export const METER_FROM = 7;

export type UsageSession = {
  startedAt: string;
  lastSeenAt: string;
  maxSlide: number;
  totalSlides: number;
};

export type UsagePresentation = {
  deckId: string;
  title: string | null;
  localDay: string;
  startedAt: string;
  lastSeenAt: string;
  // Charged against the Free allowance. False for presentations given on Pro
  // (or a retired trial), quick opens (see brief), and every presentation of a Pro account.
  counted: boolean;
  number: number | null;
  overLimit: boolean;
  // Not counted because every session was shorter than the minimum (a quick
  // open). Absent from servers that predate it.
  brief?: boolean;
  sessions: UsageSession[];
};

export type UsageReport = {
  month: string;
  timeZone: string;
  plan: "free" | "paid";
  allowance:
    | {
        used: number;
        limit: number;
        remaining: number;
        resetsAt: string;
        enforced: boolean;
      }
    | null;
  presentations: UsagePresentation[];
};

export type UsageResult = UsageReport | "unauthenticated" | "unavailable";

export const fetchUsage = async (month?: string): Promise<UsageResult> => {
  const session = getStoredHostSession();
  if (!session) return "unauthenticated";
  const params = new URLSearchParams();
  if (month) params.set("month", month);
  const tz = browserTimeZone();
  if (tz) params.set("tz", tz);
  try {
    const resp = await fetch(`${API_URL}/host/usage?${params.toString()}`, {
      headers: { Authorization: `Bearer ${session.sessionToken}` },
    });
    if (resp.status === 401) return "unauthenticated";
    if (!resp.ok) return "unavailable";
    return (await resp.json()) as UsageReport;
  } catch {
    return "unavailable";
  }
};

// A deck the signed-in host is presenting right now (the extension is driving
// it). The dashboard offers to take them straight back to its controls.
export type LivePresentation = {
  deckId: string;
  title: string | null;
  currentSlide: number;
  totalSlides: number;
};

export const fetchLive = async (): Promise<LivePresentation[] | "unauthenticated" | "unavailable"> => {
  const session = getStoredHostSession();
  if (!session) return "unauthenticated";
  try {
    const resp = await fetch(`${API_URL}/host/live`, {
      headers: { Authorization: `Bearer ${session.sessionToken}` },
    });
    if (resp.status === 401) return "unauthenticated";
    if (!resp.ok) return "unavailable";
    const body = (await resp.json()) as { presentations?: LivePresentation[] };
    return body.presentations ?? [];
  } catch {
    return "unavailable";
  }
};

// --- Formatting shared by the dashboard's views -----------------------------

export const monthKey = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

export const shiftMonth = (month: string, delta: number) => {
  const [y, m] = month.split("-").map(Number);
  return monthKey(new Date(y, m - 1 + delta, 1));
};

export const monthLabel = (month: string) => {
  const [y, m] = month.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });
};

// Counting started when session tracking shipped; earlier months are empty by
// construction, not because nothing happened.
export const FIRST_MONTH = "2026-09";

export const dayLabel = (localDay: string) => {
  const [y, m, d] = localDay.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
};

export const timeLabel = (iso: string) =>
  new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

export const minutesBetween = (a: string, b: string) =>
  Math.max(1, Math.round((new Date(b).getTime() - new Date(a).getTime()) / 60000));

export const durationLabel = (minutes: number) =>
  minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)} h ${minutes % 60} min`;

// Total time on stage across a presentation's sessions.
export const presentationMinutes = (p: UsagePresentation) =>
  p.sessions.reduce((sum, s) => sum + minutesBetween(s.startedAt, s.lastSeenAt), 0);
